/**
 * ProfileJourneyStageSection — Displays the user's selected journey stage
 * on the profile view page. Visibility-gated: if private, only the profile
 * owner can see it; if public, other members can see it too.
 *
 * When the owner has no journey stage, shows an empty-state card prompting
 * them to visit settings.
 *
 * @route /profile/:uid
 */

import React from 'react'
import ProfileIdentityCardHeader from '../../shared/ProfileIdentityCardHeader/index'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { JOURNEY_OPTIONS } from '../../../../../services/community/identity/profile/constants'

/** Static text keys for each journey stage description. */
const JOURNEY_DESCRIPTION_KEYS: Record<string, string> = {
  questioning: 'journeyQuestioningDesc',
  seeking: 'journeySeekingDesc',
  changing: 'journeyChangingDesc',
  belonging: 'journeyBelongingDesc',
  advocacy: 'journeyAdvocacyDesc',
  'not-sure': 'journeyNotSureDesc',
}

const BLOCK = 'profile-journey-stage'

export interface ProfileJourneyStageSectionProps {
  journeyStage: string | null
  /**
   * Profile field visibility. May be null/undefined for non-owner viewers —
   * the backend hides *Visibility settings from non-owners (it's owner
   * metadata), so a null value here means "no restriction known to this
   * viewer." The _data_ is independently gated by the parent's
   * isFieldVisible check; we only hide from this component when the owner
   * preview explicitly sets 'private'.
   */
  journeyStageVisibility?: string | null
  isOwner: boolean
  onSettingsClick?: () => void
}

function ProfileJourneyStageSection ({
  journeyStage,
  journeyStageVisibility,
  isOwner,
  onSettingsClick,
}: ProfileJourneyStageSectionProps) {
  const title = useTranslatedText({ keys: ['journeyStageTitle'] })
  const emptyText = useTranslatedText({ keys: ['journeyStageEmptyText'] })
  const emptyLinkText = useTranslatedText({ keys: ['journeyStageEmptyLink'] })
  const emptySuffix = useTranslatedText({ keys: ['journeyStageEmptySuffix'] })
  // Hooks are called unconditionally; only the relevant entry is read below.
  const stageNameKey = `journeyStage_${journeyStage ?? ''}`
  const descriptionKey = journeyStage ? JOURNEY_DESCRIPTION_KEYS[journeyStage] : ''
  const translatedStageName = useTranslatedText({ keys: [stageNameKey] })
  const translatedStageDescription = useTranslatedText({
    keys: [descriptionKey || stageNameKey],
  })

  const isPrivate = journeyStageVisibility === 'private'

  if (!journeyStage) {
    if (!isOwner) return null
    return (
      <div className={BLOCK}>
        <ProfileIdentityCardHeader
          className={BLOCK}
          title={title}
          isOwner={isOwner}
          isPopulated={false}
          description={emptyText}
          settingsLinkText={emptyLinkText}
          settingsLinkSuffix={emptySuffix}
          onSettingsClick={onSettingsClick}
        />
      </div>
    )
  }

  if (!isOwner && isPrivate) return null

  const journeyOption = JOURNEY_OPTIONS.find((o) => o.key === journeyStage)
  // useTranslatedText falls back to the last key on lookup miss; treat that
  // as "no translation available" and substitute a humanized form derived
  // from the journey-option key.
  let stageName: string
  if (translatedStageName !== stageNameKey) {
    stageName = translatedStageName
  } else if (journeyOption) {
    stageName = journeyOption.key.charAt(0).toUpperCase()
      + journeyOption.key.slice(1).replace('-', ' ')
  } else {
    stageName = journeyStage
  }
  const stageDescription = descriptionKey && translatedStageDescription !== descriptionKey
    ? translatedStageDescription
    : ''

  return (
    <div className={BLOCK}>
      <ProfileIdentityCardHeader
        className={BLOCK}
        title={title}
        isOwner={isOwner}
        isPopulated
      />
      <p className={`${BLOCK}__description`}>
        {stageName}{stageDescription ? ` - ${stageDescription}` : ''}
      </p>
    </div>
  )
}

export default React.memo(ProfileJourneyStageSection)
